import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    OnDestroy,
    signal,
} from '@angular/core';
import { DjangoService, Routes } from '@core/services/django.service';
import { EventTrackerService } from '@core/services/event-tracker.service';
import { ANALYTICS_EVENTS } from '@core/utils/AnalyticsEvents';
import { IncompleteTripCheckService } from '@features/client-dashboard/services/incomplete-trip-check.service';
import {
    LazyPictureComponent,
    Stops,
} from '@features/lazy-image/components/lazy-picture/lazy-picture.component';
import {
    FeaturedImageAspect,
    featuredImageConfiguration,
} from '@features/lazy-image/helpers/featured-image';
import { ModalService } from '@features/modal/services/modal.service';
import { FellowTraveler } from '@generated/openapi/model/fellowTraveler';
import { NearestIncompleteInvoice } from '@generated/openapi/model/nearestIncompleteInvoice';
import { CardSectionComponent } from '@shared/card-section/card-section.component';
import { CtaButtonComponent } from '@shared/cta-button/cta-button.component';
import { LoaderComponent } from '@shared/loader/loader.component';
import { TravelerFormComponent } from '@shared/traveler-form/traveler-form.component';
import { derivedAsync } from 'ngxtension/derived-async';

export interface UpdatedTravelerData {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    is_minor: boolean;
}

@Component({
    selector: 'app-incomplete-info-modal',
    imports: [
        CtaButtonComponent,
        CardSectionComponent,
        TravelerFormComponent,
        LazyPictureComponent,
        LoaderComponent,
    ],
    templateUrl: './incomplete-info-modal.component.html',
    styles: ``,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncompleteInfoModalComponent implements OnDestroy {
    private djangoService = inject(DjangoService);
    private modalService = inject(ModalService);
    private incompleteTripCheckService = inject(IncompleteTripCheckService);
    private eventTrackerService = inject(EventTrackerService);

    modalData: { invoiceId?: number };
    readonly modalContainerstyles = [
        'md:w-[90%]! md:h-[90%]! max-h-screen! rounded-xl! h-screen',
    ];
    readonly isModalFullscreen = true;

    // Fetch initial fellow travelers data from API
    private initialFellowTravelers = derivedAsync(() =>
        this.djangoService.drfList<FellowTraveler>(
            `${Routes.dashboard}fellow_travelers/`
        )
    );
    fellowTravelers = signal<FellowTraveler[] | undefined>(undefined);

    nearestIncompleteTrip = derivedAsync(() =>
        this.djangoService.drfGet<NearestIncompleteInvoice>(
            Routes.dashboard,
            'nearest_incomplete_trip'
        )
    );

    // Number of fellow travelers who still need information captured
    incompleteTravelerCount = computed(() => {
        const travelers = this.fellowTravelers();
        if (!travelers) {
            return 0;
        }

        return travelers.filter(traveler => traveler.is_complete === false)
            .length;
    });

    isLoading = computed(() => {
        const travelers = this.fellowTravelers();
        return (
            travelers === undefined ||
            this.nearestIncompleteTrip() === undefined
        );
    });

    constructor() {
        // Sync initial API data to local signal
        effect(() => {
            const initial = this.initialFellowTravelers();
            if (initial) {
                this.fellowTravelers.set(initial);
            }
        });

        // Track when modal is displayed
        afterNextRender(() => {
            this.eventTrackerService.trackEvent(
                ANALYTICS_EVENTS.DASHBOARD_INVITE_DISPLAYED,
                {
                    incomplete_traveler_count: this.incompleteTravelerCount(),
                }
            );
        });

        effect(() => {
            const incompleteCount = this.incompleteTravelerCount();
            const invoiceId = this.modalData?.invoiceId;

            if (incompleteCount === 0 && invoiceId) {
                this.eventTrackerService.trackEvent(
                    ANALYTICS_EVENTS.DASHBOARD_INVITE_COMPLETED,
                    {
                        incomplete_traveler_count: 0,
                    }
                );
            }
        });
    }

    // Featured image renditions for the modal's trip hero.
    tripImageConfiguration = computed(() =>
        featuredImageConfiguration(
            this.nearestIncompleteTrip()?.featured_image,
            {
                [Stops.FALLBACK]: FeaturedImageAspect.SQUARE,
                [Stops.BASE]: FeaturedImageAspect.LANDSCAPE,
                [Stops.SM]: FeaturedImageAspect.LANDSCAPE,
                [Stops.MD]: FeaturedImageAspect.BANNER,
                [Stops.LG]: FeaturedImageAspect.BANNER,
                [Stops.XL]: FeaturedImageAspect.BANNER,
                [Stops.XXL]: FeaturedImageAspect.BANNER,
            }
        )
    );

    onContinueToDashboard() {
        this.modalService.hide();
        // Refresh the incomplete trip check after closing the modal
        this.incompleteTripCheckService.refresh();
    }

    getTravelerWithPosition(
        traveler: FellowTraveler,
        position: number
    ): FellowTraveler & { position: number } {
        return {
            ...traveler,
            position,
        };
    }

    // Update local traveler data after successful form submission
    onTravelerUpdated(updatedData: UpdatedTravelerData) {
        const currentTravelers = this.fellowTravelers();
        if (!currentTravelers) return;

        // Find and update the specific traveler in local state
        const updatedTravelers = currentTravelers.map(traveler => {
            if (traveler.id === updatedData.id) {
                return {
                    ...traveler,
                    ...updatedData,
                    is_complete: true, // Mark as complete after successful submission
                };
            }
            return traveler;
        });

        // Update the local signal
        this.fellowTravelers.set(updatedTravelers);
    }

    ngOnDestroy(): void {
        // Only track abandoned if they didn't complete the form
        if (this.incompleteTravelerCount() > 0) {
            this.eventTrackerService.trackEvent(
                ANALYTICS_EVENTS.DASHBOARD_INVITE_ABANDONED,
                {
                    incomplete_traveler_count: this.incompleteTravelerCount(),
                }
            );
        }
    }
}
